// Recombination (HER-4, ARCH §28.5). PURE (ENG-1).
//
// A child's genome is its parents' genomes shuffled by the seeded RNG — one allele from
// each parent per Mendelian gene, the parents' mean plus triangular noise per polygenic
// gene — then mutated at `HER.MUTATION` per gene. The RNG is forked by
// `(min id, max id, tick)`, so the same two parents on the same tick always make the same
// child and the order they found each other in cannot matter.
//
// Draw order, fixed and documented because it IS the determinism contract: genes in
// table order; for each gene its inheritance draws first, then its mutation draw(s).
import { HER } from '../config';
import { tripleAverage } from '../game/horse';
import { genomeLength } from './genome';
import type { Rng } from '../engine/rng';
import type { Gene } from './genes';

const clampByte = (v: number): number => Math.max(0, Math.min(255, v));

/** The child of `a` × `b` under `genes` (both parents laid out by it). `mutation` is the
 *  per-gene chance, `HER.MUTATION` unless a test pins it (e.g. 0, to see the laws bare). */
export function recombine(
  genes: readonly Gene[], a: Uint8Array, b: Uint8Array, rng: Rng, mutation: number = HER.MUTATION,
): Uint8Array {
  const out = new Uint8Array(genomeLength(genes));
  let at = 0;
  for (const g of genes) {
    if (g.kind === 'mendelian') {
      // 1. One allele from each parent's pair, each by a fair coin.
      let x = a[at + rng.nextInt(2)]!;
      let y = b[at + rng.nextInt(2)]!;
      // 3. Mutation: replace ONE allele (which, by a coin) with any allele of the gene.
      if (rng.nextFloat() < mutation) {
        const allele = rng.nextInt(g.alleles.length);
        if (rng.nextInt(2) === 0) x = allele; else y = allele;
      }
      out[at] = Math.min(x, y);
      out[at + 1] = Math.max(x, y);
      at += 2;
    } else {
      // 2. The parents' mean plus triangular noise on [−NOISE, +NOISE], from the same
      // triple-average shape a wild roll uses.
      const noise = Math.round(tripleAverage(rng.nextFloat(), rng.nextFloat(), rng.nextFloat()) * 2 * HER.NOISE - HER.NOISE);
      let v = clampByte(Math.round((a[at]! + b[at]!) / 2 + noise));
      // 3. Mutation: a step of ±MUTATION_STEP, the sign by a coin.
      if (rng.nextFloat() < mutation) v = clampByte(v + (rng.nextInt(2) === 0 ? -1 : 1) * HER.MUTATION_STEP);
      out[at] = v;
      at += 1;
    }
  }
  return out;
}

/** Two species share a table when their tables are the same genes in the same order —
 *  the only case in which one's genome means anything to the other (E-52). */
export function sameTable(a: readonly Gene[] | undefined, b: readonly Gene[] | undefined): boolean {
  if (!a || !b || a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/** What a conversion (tadpole → frog, and any later metamorphosis) carries across, as
 *  `spawn` options: the genome and parents when both species share a table, else nothing
 *  — dropped silently (E-52). */
export function carryAcross(
  from: { genome?: Uint8Array; parents?: [number, number] },
  fromGenes: readonly Gene[] | undefined,
  toGenes: readonly Gene[] | undefined,
): { genome?: Uint8Array; parents?: [number, number] } {
  if (!from.genome || !sameTable(fromGenes, toGenes)) return {};
  return { genome: from.genome, ...(from.parents ? { parents: from.parents } : {}) };
}
